import { Injectable } from '@angular/core';
import { map, switchMap } from 'rxjs';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';

import { CreateNotificationApiService } from '../services/create-notification-api.service';
import { UserStoreService } from '@auth/store/services/user-store.service';
import * as externallistingsActions from '@external-listings/store/actions/external-listings.actions';
import * as listingsActions from '@listings/store/actions/listings.actions';
import { NotificationEventEntity } from '@notifications/enums/notification-event-entity';
import * as notificationsActions from '../actions/notifications.actions';

@Injectable()
export class CreateListingNotificationEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly createNotificationApiService: CreateNotificationApiService,
        private readonly userStoreService: UserStoreService,
    ) { }

    public readonly createExternalListingAddedNotification$ = createEffect(
        () => this.actions$.pipe(
            ofType(externallistingsActions.createListingSuccess),
            concatLatestFrom(() => this.userStoreService.customerCollaborationId$),
            switchMap(([{ listing }, collaborationId]) => {

                const externalListingAddedNotification = {
                    externalListingId: listing.id,
                    collaborationId,
                    createId: listing.createId
                };

                return this.createNotificationApiService.createExternalListingAddedNotification(externalListingAddedNotification);
            })
        )
    );

    public readonly removeExternalListingNotification$ = createEffect(
        () => this.actions$.pipe(
            ofType(externallistingsActions.deleteListingSuccess),
            map(({ deletedListingId }) => {
                const removeExternalListingNotificationInput = {
                    entityType: NotificationEventEntity.ExternalListing,
                    entitiesIds: [deletedListingId]
                };

                return notificationsActions.removeEntitiesNotifications(removeExternalListingNotificationInput);
            })
        )
    );

    public readonly createExternalListingRemovedNotification$ = createEffect(
        () => this.actions$.pipe(
            ofType(externallistingsActions.deleteListingSuccess),
            concatLatestFrom(() => this.userStoreService.customerCollaborationId$),
            switchMap(([{ deletedListingId }, collaborationId]) => {

                const externalListingDeletedNotification = {
                    externalListingId: deletedListingId,
                    collaborationId
                };

                return this.createNotificationApiService.createExternalListingRemovedNotification(externalListingDeletedNotification);
            })
        )
    );

    public readonly createListingHardDeletedNotification$ = createEffect(() => this.actions$.pipe(
        ofType(listingsActions.hardDeleteSuccess),
        concatLatestFrom(() => [
            this.userStoreService.customerCollaborationId$,
            this.userStoreService.customerId$,
        ]),
        switchMap(([{ listingsHashCodes }, collaborationId, customerId]) => {
            const input = { listingIds: listingsHashCodes, collaborationId, createId: customerId };

            return this.createNotificationApiService.createListingHardDeletedNotification(input);
        })
    ));

    public readonly createListingSharedNotification$ = createEffect(() => this.actions$.pipe(
        ofType(listingsActions.listingShared),
        concatLatestFrom(() => this.userStoreService.customerCollaborationId$),
        switchMap(([{ listingId }, collaborationId]) => {
            const input = { listingId: listingId, collaborationId };
            return this.createNotificationApiService.createListingSharedNotification(input);
        })
    ));

}